/**
 * Caregiver ClientDetailPage (CONTRACTS §6, spec §9.3): profile basics, a
 * Monday-first week selector, the read-only 24-hour medication agenda,
 * links to the schedule / intakes / proofs subpages, and the audit history
 * for this client.
 *
 * The clientId comes from the route; every repository call re-scopes
 * server-side via RLS, so an id the caregiver is not linked to simply
 * returns nothing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, EmptyState, ErrorState, FeatureCard, IconButton, Skeleton } from '@/components/ui'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  PillIcon,
  ShieldIcon,
  UserIcon,
  VideoIcon,
} from '@/components/icons'
import { repositories } from '@/services/repositories'
import { PATHS } from '@/app/router/paths'
import type { AuditEvent } from '@/types'
import {
  addDaysISO,
  formatDutchDateTime,
  formatShortDutchDay,
  isValidISODate,
  todayLocalISODate,
  weekDaysISO,
  weekStartISO,
} from '@/features/medication'
import { MedicationAgenda } from '@/features/client'
import { useLinkedClients } from './useLinkedClients'
import './caregiver.css'

const DAY_LETTERS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'] as const

export default function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const { status, clients, refresh } = useLinkedClients()
  const [selectedDate, setSelectedDate] = useState(todayLocalISODate())
  const [weekAnchor, setWeekAnchor] = useState(todayLocalISODate())

  const client = useMemo(
    () => clients.find((candidate) => candidate.id === clientId) ?? null,
    [clients, clientId],
  )

  const weekStart = weekStartISO(weekAnchor)
  const days = weekDaysISO(weekStart)
  const today = todayLocalISODate()

  const moveWeek = (delta: number) => {
    const nextAnchor = addDaysISO(weekAnchor, delta * 7)
    setWeekAnchor(nextAnchor)
    setSelectedDate(nextAnchor)
  }

  if (!clientId) {
    return (
      <EmptyState
        icon={<UserIcon size={26} />}
        title="Cliënt niet gevonden"
        body="Deze koppeling bestaat niet of is beëindigd."
        action={
          <button type="button" className="caregiver-back-link" onClick={() => navigate(PATHS.caregiver.clients)}>
            Terug naar cliënten
          </button>
        }
      />
    )
  }

  return (
    <div className="caregiver-page">
      <button
        type="button"
        className="caregiver-back-link"
        onClick={() => navigate(PATHS.caregiver.clients)}
      >
        <ChevronLeftIcon size={16} />
        Terug naar cliënten
      </button>

      {status === 'loading' ? <Skeleton lines={3} /> : null}
      {status === 'unavailable' ? (
        <Card padding="md">
          <p className="caregiver-muted">Cloud niet ingesteld. De cliëntgegevens kunnen nu niet worden opgehaald.</p>
        </Card>
      ) : null}
      {status === 'error' ? <ErrorState onRetry={() => void refresh()} /> : null}

      {status === 'ready' ? (
        client === null ? (
          <EmptyState
            icon={<UserIcon size={26} />}
            title="Geen actieve koppeling"
            body="Je bent niet (meer) gekoppeld aan deze cliënt. Vraag de cliënt om de koppeling opnieuw te accepteren."
            action={
              <button
                type="button"
                className="caregiver-back-link"
                onClick={() => navigate(PATHS.caregiver.clients)}
              >
                Naar koppelingen
              </button>
            }
          />
        ) : (
          <>
            {/* Profile basics */}
            <Card padding="md">
              <div className="caregiver-detail-header">
                <span className="caregiver-client-avatar" aria-hidden="true">
                  <UserIcon size={20} />
                </span>
                <div>
                  <h1 className="caregiver-detail-name">{client.displayName?.trim() || 'Cliënt'}</h1>
                  <p className="caregiver-muted">
                    Tijdzone: {client.timezone} · Taal: {client.locale}
                  </p>
                </div>
              </div>
            </Card>

            {/* Subpage navigation */}
            <nav className="caregiver-subnav" aria-label="Cliënt onderdelen">
              <FeatureCard
                icon={<PillIcon size={20} />}
                title="Schema"
                onClick={() => navigate(PATHS.caregiver.clientSchedule(client.id))}
              />
              <FeatureCard
                icon={<ClockIcon size={20} />}
                title="Innames"
                onClick={() => navigate(PATHS.caregiver.clientIntakes(client.id))}
              />
              <FeatureCard
                icon={<VideoIcon size={20} />}
                title="Bewijs"
                onClick={() => navigate(PATHS.caregiver.clientProofs(client.id))}
              />
            </nav>

            {/* Week selector */}
            <Card padding="sm">
              <div className="caregiver-week-nav">
                <IconButton aria-label="Vorige week" variant="ghost" onClick={() => moveWeek(-1)}>
                  <ChevronLeftIcon size={20} />
                </IconButton>
                <span className="caregiver-week-label">
                  Week van {formatShortDutchDay(weekStart)}
                </span>
                <IconButton aria-label="Volgende week" variant="ghost" onClick={() => moveWeek(1)}>
                  <ChevronRightIcon size={20} />
                </IconButton>
              </div>
              <div className="caregiver-week-days" role="group" aria-label="Kies een dag">
                {days.map((day, index) => {
                  const dayNumber = Number(day.split('-')[2])
                  return (
                    <button
                      key={day}
                      type="button"
                      className={`caregiver-week-day${day === today ? ' caregiver-week-day--today' : ''}`}
                      aria-pressed={day === selectedDate}
                      aria-label={formatShortDutchDay(day)}
                      onClick={() => {
                        setSelectedDate(day)
                        setWeekAnchor(day)
                      }}
                    >
                      <span>{DAY_LETTERS[index]}</span>
                      <strong>{dayNumber}</strong>
                    </button>
                  )
                })}
              </div>
            </Card>

            {/* Read-only 24-hour agenda */}
            {isValidISODate(selectedDate) ? (
              <MedicationAgenda date={selectedDate} clientId={client.id} readOnly />
            ) : null}

            {/* Audit history */}
            <ClientAuditSection clientId={client.id} />
          </>
        )
      ) : null}
    </div>
  )
}

function ClientAuditSection({ clientId }: { clientId: string }) {
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; events: AuditEvent[] }>({
    status: 'loading',
    events: [],
  })

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading' }))
    try {
      const events = await repositories.caregiver.listAuditForClient(clientId, 30)
      setState({ status: 'ready', events })
    } catch {
      setState({ status: 'error', events: [] })
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="caregiver-section" aria-labelledby="cg-audit">
      <h2 id="cg-audit" className="caregiver-section-title">
        <ShieldIcon size={18} /> Auditgeschiedenis
      </h2>
      {state.status === 'loading' ? <Skeleton lines={2} /> : null}
      {state.status === 'error' ? (
        <Card padding="sm">
          <div className="caregiver-actions">
            <p className="caregiver-muted">De auditgeschiedenis kon niet laden.</p>
            <button type="button" className="caregiver-back-link" onClick={() => void load()}>
              Opnieuw proberen
            </button>
          </div>
        </Card>
      ) : null}
      {state.status === 'ready' ? (
        state.events.length === 0 ? (
          <p className="caregiver-muted">Nog geen gebeurtenissen vastgelegd voor deze cliënt.</p>
        ) : (
          <Card padding="sm">
            <ul className="caregiver-list" aria-label="Auditgeschiedenis">
              {state.events.map((event) => (
                <li key={event.id} className="caregiver-audit-item">
                  <span className="caregiver-audit-action">{event.action}</span>
                  <span className="caregiver-audit-meta">
                    {event.entityType}
                    {event.entityId ? ` · ${event.entityId.slice(0, 8)}…` : ''} ·{' '}
                    {formatDutchDateTime(event.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )
      ) : null}
    </section>
  )
}
